"use client";

import { FloatingLines } from "./floating-lines";

const BRAND_VIOLET = "#a78bfa";

export type AmbientIntensity = "default" | "muted";

const INTENSITY_PRESETS: Record<
  AmbientIntensity,
  {
    opacity: string;
    lineCount: number;
    blurPx: number;
    saturate: number;
    /** Radial mask that carves out a darker spot in the center to keep
     *  text legible. `none` disables the mask. */
    mask: "none" | "spot";
  }
> = {
  default: {
    opacity: "opacity-70",
    lineCount: 5,
    blurPx: 10,
    saturate: 1.4,
    mask: "none",
  },
  muted: {
    opacity: "opacity-30",
    lineCount: 2,
    blurPx: 14,
    saturate: 1.2,
    mask: "spot",
  },
};

const SPOT_MASK =
  "radial-gradient(ellipse 60% 80% at center, transparent 0%, transparent 25%, black 80%)";

/**
 * Sticky ambient background: violet floating lines + frosted-glass layer.
 *
 * `intensity="muted"` dims everything, drops to 2 lines, and applies a
 * radial mask to carve a darker reading spot in the middle, use it on
 * detail pages where readability matters more than ambient identity.
 */
export function AmbientFloatingLines({
  intensity = "default",
}: {
  intensity?: AmbientIntensity;
}) {
  const preset = INTENSITY_PRESETS[intensity];
  const maskValue = preset.mask === "spot" ? SPOT_MASK : undefined;

  return (
    <>
      <div
        className={`pointer-events-none sticky top-0 -mb-[100vh] h-screen z-0 ${preset.opacity}`}
        style={
          maskValue
            ? {
                maskImage: maskValue,
                WebkitMaskImage: maskValue,
              }
            : undefined
        }
      >
        <FloatingLines
          linesGradient={[BRAND_VIOLET]}
          enabledWaves={["top", "middle", "bottom"]}
          lineCount={[preset.lineCount, preset.lineCount, preset.lineCount]}
          lineDistance={[5, 5, 5]}
          animationSpeed={0.3}
          interactive={false}
          parallax={false}
          mixBlendMode="screen"
        />
      </div>

      <div
        className="pointer-events-none sticky top-0 -mb-[100vh] h-screen z-[1]"
        style={{
          backdropFilter: `blur(${preset.blurPx}px) saturate(${preset.saturate})`,
          WebkitBackdropFilter: `blur(${preset.blurPx}px) saturate(${preset.saturate})`,
        }}
      />
    </>
  );
}
